import { isDefined } from '@mfw/common';

import type {
  ApiQuestionnaireSubmission,
  ApiQuestionnaireAnswer,
} from '../../schemas/questionnaireSubmissions.schema';
import type {
  ApiQuestionnaireQuestion,
  ApiQuestionnaire,
} from '../../schemas/questionnaires.schema';

/**
 * Postgres DECIMAL columns round-trip through the API as strings. The
 * questionnaire builder already coerces them on the way into its form
 * (`questionnaires.schema.ts`), but the answering side reads the API shape
 * directly: an uncoerced `numberValue` fails the `z.number()` field schema on
 * a question the user can plainly see is filled in, and an uncoerced
 * `scaleMin`/`scaleMax` renders as "1.000" in labels and range messages.
 */
const toNumber = (value: unknown) =>
  !isDefined(value) || value === '' ? null : Number(value);

const mapQuestionFromApi = (
  question: ApiQuestionnaireQuestion,
): ApiQuestionnaireQuestion => ({
  ...question,
  numberMax: toNumber(question.numberMax),
  numberMin: toNumber(question.numberMin),
  scaleMax: toNumber(question.scaleMax),
  scaleMin: toNumber(question.scaleMin),
  scaleStep: toNumber(question.scaleStep),
});

const mapAnswerFromApi = (
  answer: ApiQuestionnaireAnswer,
): ApiQuestionnaireAnswer => ({
  ...answer,
  numberValue: toNumber(answer.numberValue),
});

export const mapQuestionnaireWithQuestionsFromApi = (
  questionnaire: ApiQuestionnaire,
): ApiQuestionnaire => ({
  ...questionnaire,
  questions: (questionnaire?.questions ?? []).map(mapQuestionFromApi),
});

export const mapQuestionnaireSubmissionFromApi = (
  submission: ApiQuestionnaireSubmission,
): ApiQuestionnaireSubmission => ({
  ...submission,
  answers: (submission?.answers ?? []).map(mapAnswerFromApi),
  questionnaire: mapQuestionnaireWithQuestionsFromApi(submission.questionnaire),
});
